import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { RefreshCcw, Users, User, TrendingDown, AlertTriangle, XCircle, FileDown, Bell, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  getPendencyByEngineer, getPendencyByCustomer, getShortClosedReport, getFirms,
  exportPendencyExcel, getSalesOrder, sendFlowNotification,
} from '../../services/taskApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fileLink = (u) => (u && u.startsWith('http') ? u : `${API_URL}${u}`);

const stuckParts = (b) => {
  if (!b) return [];
  const parts = [];
  if (b.purchase) parts.push([`${b.purchase} in Purchase`, 'purchase_user']);
  if (b.approval) parts.push([`${b.approval} awaiting Admin approval`, 'admin']);
  if (b.billing) parts.push([`${b.billing} at Billing (allocated, unbilled)`, 'billing_user']);
  if (b.store_billing) parts.push([`${b.store_billing} at Store`, 'store_user']);
  if (b.logistics) parts.push([`${b.logistics} at Logistics (billed, not shipped)`, 'logistics_user']);
  if (b.in_transit) parts.push([`${b.in_transit} in transit`, 'logistics_user']);
  if (b.short_closed) parts.push([`${b.short_closed} short-closed`, 'admin']);
  return parts;
};

const StageColor = ({ status }) => {
  const map = {
    'Awaiting Approval': 'bg-yellow-500/15 text-yellow-500',
    'Approved': 'bg-blue-500/15 text-blue-500',
    'Awaiting Tally Check': 'bg-cyan-500/15 text-cyan-500',
    'Awaiting Procurement': 'bg-orange-500/15 text-orange-500',
    'Ready to Pack': 'bg-indigo-500/15 text-indigo-500',
    'Billed': 'bg-purple-500/15 text-purple-500',
    'Awaiting Dispatch': 'bg-pink-500/15 text-pink-500',
    'Pushed to Logistics': 'bg-pink-500/15 text-pink-500',
    'Dispatched': 'bg-teal-500/15 text-teal-500',
    'Partially Dispatched': 'bg-teal-500/15 text-teal-500',
    'Partially Delivered': 'bg-emerald-500/15 text-emerald-500',
    'Short Closed': 'bg-red-500/15 text-red-500',
  };
  const cls = map[status] || 'bg-gray-500/15 text-gray-500';
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>{status}</span>;
};

const PendencyRow = ({ r, isDark, showEngineer = false, onPreview, onNotify }) => {
  const overdue = typeof r.days_pending === 'number' && r.days_pending > 14;
  const parts = stuckParts(r.pending_breakdown);
  return (
    <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-testid={`pendency-row-${r.item_id}`}>
      <td className={`py-2 px-2 font-medium text-xs`}>
        <button onClick={() => onPreview && onPreview(r)} className={`underline-offset-2 hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
          title="View SO preview (customer PO & order detail)" data-testid={`so-preview-${r.item_id}`}>
          {r.so_number}
        </button>
      </td>
      <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{showEngineer ? (r.engineer_name || r.engineer_email || '—') : r.customer_name}</td>
      <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/90' : 'text-gray-900'}`}>
        {r.item_name}
        {r.purchase_item_name && <div className="text-[9px] text-purple-400" title="Purchase team's item name">Purch: {r.purchase_item_name}</div>}
        <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`} data-testid={`qty-progress-${r.item_id}`}>
          Qty: {r.quantity_ordered} ord{r.quantity_approved ? ` · ${r.quantity_approved} appr` : ''} · {r.quantity_billed || 0} billed · {r.quantity_dispatched || 0} shipped · {r.quantity_delivered || 0} delv
          {(r.quantity_short_closed || 0) > 0 && <span className="text-red-500 font-semibold"> · {r.quantity_short_closed} short-closed</span>}
        </div>
        {(r.quantity_short_closed || 0) > 0 && r.short_close_reason && (
          <div className="text-[10px] text-red-400/90 italic" data-testid={`short-close-reason-${r.item_id}`} title="Short-close reason">
            Short-close{r.short_closed_stage ? ` (${r.short_closed_stage})` : ''}: {r.short_close_reason}
          </div>
        )}
        {parts.length > 0 && (
          <div className="text-[10px] text-orange-400 font-medium" data-testid={`stuck-${r.item_id}`}>
            Stuck: {parts.map(p => p[0]).join(' · ')}
            {r.pending_value > 0 && <span className="text-blue-400"> · worth ₹{Math.round(r.pending_value).toLocaleString('en-IN')}</span>}
          </div>
        )}
      </td>
      <td className="py-2 px-2"><StageColor status={r.fulfillment_status} /></td>
      <td className="py-2 px-2 text-xs">
        <span className={overdue ? 'text-red-500 font-semibold' : (isDark ? 'text-white/60' : 'text-gray-600')}>
          {r.days_pending === null ? '—' : `${r.days_pending} d`}
        </span>
        {overdue && <AlertTriangle className="inline-block ml-1 text-red-500" size={11} />}
      </td>
      <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
        {r.expected_arrival ? (
          <span data-testid={`eta-${r.item_id}`}>{r.expected_arrival}</span>
        ) : (
          <span className="text-orange-500" data-testid={`no-eta-${r.item_id}`}>
            {r.procurement_note || 'Pending in Procurement'}
          </span>
        )}
      </td>
      <td className="py-2 px-2 text-right">
        {onNotify && (
          <button onClick={() => onNotify(r)} className="p-1 rounded-md text-amber-500 hover:bg-amber-500/10"
            title="Send reminder notification to the responsible department" data-testid={`notify-${r.item_id}`}>
            <Bell size={13} />
          </button>
        )}
      </td>
    </tr>
  );
};

const FlowPendencyReport = () => {
  const { isDark, user } = useOutletContext();
  const [tab, setTab] = useState('engineer');
  const [engineerData, setEngineerData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [shortData, setShortData] = useState(null);
  const [firms, setFirms] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);        // { loading, order }
  const [notify, setNotify] = useState(null);          // { row, to_role, message, sending }

  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [engRes, firmsRes] = await Promise.all([
        getPendencyByEngineer(filterCompany || undefined),
        firms.length ? Promise.resolve({ data: firms }) : getFirms(),
      ]);
      setEngineerData(engRes.data);
      if (!firms.length) setFirms(firmsRes.data || []);
      if (isAdmin && tab === 'customer') {
        const custRes = await getPendencyByCustomer(filterCompany || undefined);
        setCustomerData(custRes.data);
      }
      if (tab === 'short_closed') {
        const scRes = await getShortClosedReport(filterCompany || undefined);
        setShortData(scRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterCompany, tab, isAdmin, firms]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportPendencyExcel(filterCompany || undefined);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pendency_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const openPreview = async (r) => {
    setPreview({ loading: true, order: null });
    try {
      const res = await getSalesOrder(r.so_id);
      setPreview({ loading: false, order: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load SO');
      setPreview(null);
    }
  };

  const openNotify = (r) => {
    const parts = stuckParts(r.pending_breakdown);
    const toRole = parts.length ? parts[0][1] : 'admin';
    const stuckTxt = parts.map(p => p[0]).join(' · ') || r.fulfillment_status;
    setNotify({
      row: r,
      to_role: toRole,
      message: `Reminder: ${r.so_number} — ${r.item_name} (${r.customer_name})\nStatus: ${stuckTxt}\nPlease update / expedite.`,
      sending: false,
    });
  };

  const sendNotify = async (e) => {
    e.preventDefault();
    if (!notify?.message.trim()) return;
    setNotify(n => ({ ...n, sending: true }));
    try {
      const res = await sendFlowNotification({
        to_role: notify.to_role,
        message: notify.message.trim(),
        title: 'Pendency Reminder',
        so_number: notify.row.so_number,
        item_id: notify.row.item_id,
        item_name: notify.row.item_name,
      });
      toast.success(res.data.message || 'Reminder sent');
      setNotify(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
      setNotify(n => ({ ...n, sending: false }));
    }
  };

  const bgTable = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200';
  const thCls = `text-[10px] uppercase tracking-wider text-left px-2 py-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`;

  return (
    <div className="space-y-4" data-testid="flow-pendency-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pendency Report</h1>
          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {isAdmin ? 'Every open SO item and where it is stuck' : 'Your open SO items'}
          </p>
        </div>
        <div className="flex gap-2">
          <select className={`h-8 rounded-md text-xs px-2 border ${isDark ? 'bg-white/5 text-white border-white/10' : 'bg-white border-gray-200'}`}
            value={filterCompany} onChange={e => setFilterCompany(e.target.value)} data-testid="pendency-company-filter">
            <option value="">All Companies</option>
            {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="pendency-refresh">
            <RefreshCcw size={12} className="mr-1" /> Refresh
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={handleExport} disabled={exporting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="pendency-export-excel">
              <FileDown size={12} className="mr-1" /> {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="engineer" data-testid="tab-by-engineer"><User size={12} className="mr-1" /> By Engineer</TabsTrigger>
          {isAdmin && <TabsTrigger value="customer" data-testid="tab-by-customer"><Users size={12} className="mr-1" /> By Customer</TabsTrigger>}
          <TabsTrigger value="short_closed" data-testid="tab-short-closed"><XCircle size={12} className="mr-1" /> Short Closed</TabsTrigger>
        </TabsList>

        <TabsContent value="engineer">
          {loading ? (
            <div className={`text-center py-8 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading…</div>
          ) : !engineerData || engineerData.total_pending_items === 0 ? (
            <Card className={bgTable}><CardContent className="p-8 text-center">
              <TrendingDown className="mx-auto mb-2 text-green-500" size={32} />
              <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Nothing pending 🎉</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              <div className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                <strong>{engineerData.total_pending_items}</strong> pending item(s) across <strong>{engineerData.by_engineer.length}</strong> engineer(s)
                {engineerData.total_pending_value > 0 && <> · pending value <strong className="text-orange-500" data-testid="total-pending-value">₹{Math.round(engineerData.total_pending_value).toLocaleString('en-IN')}</strong></>}
              </div>
              {engineerData.by_engineer.map(bucket => (
                <Card key={bucket.engineer_email} className={bgTable} data-testid={`eng-bucket-${bucket.engineer_email}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{bucket.engineer_name || bucket.engineer_email}</div>
                        <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{bucket.engineer_email}</div>
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded-full bg-pink-500/15 text-pink-500">{bucket.count} pending</span>
                      {bucket.pending_value > 0 && <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-500/15 text-orange-500">₹{Math.round(bucket.pending_value).toLocaleString('en-IN')}</span>}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr>
                            <th className={thCls}>SO #</th>
                            <th className={thCls}>Customer</th>
                            <th className={thCls}>Item</th>
                            <th className={thCls}>Stage</th>
                            <th className={thCls}>Days</th>
                            <th className={thCls}>Expected Arrival</th>
                            <th className={thCls}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {bucket.items.map(r => <PendencyRow key={r.item_id} r={r} isDark={isDark} onPreview={openPreview} onNotify={openNotify} />)}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="customer">
            {loading ? (
              <div className={`text-center py-8 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading…</div>
            ) : !customerData || customerData.total_pending_items === 0 ? (
              <Card className={bgTable}><CardContent className="p-8 text-center">
                <TrendingDown className="mx-auto mb-2 text-green-500" size={32} />
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Nothing pending 🎉</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                <div className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  <strong>{customerData.total_pending_items}</strong> pending item(s) across <strong>{customerData.by_customer.length}</strong> customer(s)
                  {customerData.total_pending_value > 0 && <> · pending value <strong className="text-orange-500">₹{Math.round(customerData.total_pending_value).toLocaleString('en-IN')}</strong></>}
                </div>
                {customerData.by_customer.map(bucket => (
                  <Card key={bucket.customer_name} className={bgTable} data-testid={`cust-bucket-${bucket.customer_name}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{bucket.customer_name}</div>
                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-500/15 text-orange-500">{bucket.count} pending</span>
                        {bucket.pending_value > 0 && <span className="text-xs font-bold px-2 py-1 rounded-full bg-blue-500/15 text-blue-500">₹{Math.round(bucket.pending_value).toLocaleString('en-IN')}</span>}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr>
                              <th className={thCls}>SO #</th>
                              <th className={thCls}>Engineer</th>
                              <th className={thCls}>Item</th>
                              <th className={thCls}>Stage</th>
                              <th className={thCls}>Days</th>
                              <th className={thCls}>Expected Arrival</th>
                              <th className={thCls}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {bucket.items.map(r => <PendencyRow key={r.item_id} r={r} isDark={isDark} showEngineer onPreview={openPreview} onNotify={openNotify} />)}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
        <TabsContent value="short_closed">
          {loading ? (
            <div className={`text-center py-8 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading…</div>
          ) : !shortData || shortData.total === 0 ? (
            <Card className={bgTable}><CardContent className="p-8 text-center">
              <TrendingDown className="mx-auto mb-2 text-green-500" size={32} />
              <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>No short-closed items</p>
            </CardContent></Card>
          ) : (
            <Card className={bgTable} data-testid="short-closed-table">
              <CardContent className="p-3">
                <div className={`text-xs mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  <strong>{shortData.total}</strong> short-closed item(s){!isAdmin && ' on your Sales Orders'}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[700px]">
                    <thead>
                      <tr>
                        <th className={thCls}>SO #</th>
                        <th className={thCls}>Customer</th>
                        <th className={thCls}>Item</th>
                        <th className={thCls}>Qty Closed</th>
                        <th className={thCls}>Closed By</th>
                        <th className={thCls}>Reason</th>
                        <th className={thCls}>Engineer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortData.rows.map(r => (
                        <tr key={r.item_id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-testid={`short-closed-row-${r.item_id}`}>
                          <td className={`py-2 px-2 font-medium text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{r.so_number}</td>
                          <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{r.customer_name}</td>
                          <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/90' : 'text-gray-900'}`}>{r.item_name} <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>({r.quantity_ordered} ordered)</span></td>
                          <td className="py-2 px-2 text-xs font-semibold text-red-500">{r.quantity_short_closed}</td>
                          <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{r.closed_by_stage}</td>
                          <td className={`py-2 px-2 text-xs max-w-[240px] ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{r.reason || '—'}</td>
                          <td className={`py-2 px-2 text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{r.engineer_name || r.engineer_email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* SO Preview Dialog — customer PO & order detail */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className={`max-w-2xl max-h-[85vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="so-preview-dialog">
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>
              {preview?.order ? `${preview.order.so_number} — ${preview.order.customer_name}` : 'Sales Order'}
            </DialogTitle>
          </DialogHeader>
          {preview?.loading ? (
            <div className={`text-center py-8 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Loading…</div>
          ) : preview?.order && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><span className={isDark ? 'text-white/40' : 'text-gray-400'}>Customer</span><p className="font-medium">{preview.order.customer_name}</p></div>
                <div><span className={isDark ? 'text-white/40' : 'text-gray-400'}>Agent</span><p className="font-medium">{preview.order.agent_name || '—'}</p></div>
                <div><span className={isDark ? 'text-white/40' : 'text-gray-400'}>Order Date</span><p className="font-medium">{(preview.order.created_at || '').slice(0, 10)}</p></div>
                <div><span className={isDark ? 'text-white/40' : 'text-gray-400'}>Customer PO #</span><p className="font-medium">{preview.order.customer_po_number || '—'}</p></div>
                <div><span className={isDark ? 'text-white/40' : 'text-gray-400'}>PO Date</span><p className="font-medium">{preview.order.po_date || '—'}</p></div>
                <div>
                  <span className={isDark ? 'text-white/40' : 'text-gray-400'}>PO Proof</span>
                  <p>
                    {preview.order.po_proof_url ? (
                      <a href={fileLink(preview.order.po_proof_url)} target="_blank" rel="noreferrer" className="text-blue-400 font-medium inline-flex items-center gap-1" data-testid="so-preview-po-link">
                        View PO <ExternalLink size={11} />
                      </a>
                    ) : '—'}
                  </p>
                </div>
              </div>
              {preview.order.notes && <div className={`p-2 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>Notes: {preview.order.notes}</div>}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className={thCls}>Item</th>
                      <th className={thCls}>Qty</th>
                      <th className={thCls}>Net Price</th>
                      <th className={thCls}>Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.order.items || []).map(it => (
                      <tr key={it.id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                        <td className="py-1.5 px-2">{it.item_name}{it.purchase_item_name && <span className="text-purple-400 text-[9px]"> · Purch: {it.purchase_item_name}</span>}</td>
                        <td className="py-1.5 px-2">{it.quantity_ordered}</td>
                        <td className="py-1.5 px-2">₹{(it.net_price || 0).toLocaleString('en-IN')}</td>
                        <td className="py-1.5 px-2"><StageColor status={it.fulfillment_status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Reminder Notification Dialog */}
      <Dialog open={!!notify} onOpenChange={(v) => { if (!v) setNotify(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`} data-testid="notify-dialog">
          {notify && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>Send reminder — {notify.row.so_number}</DialogTitle>
              </DialogHeader>
              <form onSubmit={sendNotify} className="space-y-3 mt-2">
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>To Department</Label>
                  <select
                    className={`mt-1 w-full h-9 rounded-md text-xs px-2 border ${isDark ? 'bg-white/5 text-white border-white/10' : 'bg-white border-gray-200'}`}
                    value={notify.to_role} onChange={e => setNotify(n => ({ ...n, to_role: e.target.value }))}
                    data-testid="notify-department">
                    <option value="purchase_user">Purchase</option>
                    <option value="store_user">Store</option>
                    <option value="billing_user">Billing</option>
                    <option value="logistics_user">Logistics</option>
                    <option value="sales_agent">Sales Agents</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <Label className={isDark ? 'text-white/60' : ''}>Message</Label>
                  <textarea
                    rows={4}
                    className={`mt-1 w-full rounded-md text-xs p-2 border ${isDark ? 'bg-white/5 text-white border-white/10' : 'bg-white border-gray-200'}`}
                    value={notify.message} onChange={e => setNotify(n => ({ ...n, message: e.target.value }))}
                    data-testid="notify-message" />
                </div>
                <div className="flex gap-3 pt-1">
                  <Button type="button" variant="outline" onClick={() => setNotify(null)} className={`flex-1 h-9 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
                  <Button type="submit" disabled={notify.sending} className="flex-1 h-9 bg-amber-600 hover:bg-amber-700 text-white" data-testid="notify-send">
                    <Bell size={13} className="mr-1" /> {notify.sending ? 'Sending…' : 'Send Reminder'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowPendencyReport;
